/**
 * Convert number of seconds into string representation of hours, minutes, seconds
 * @param seconds {number}
 * @returns {string} Given seconds, formatted as HHh MMm SSs
 */
export function secondsToTimeString(seconds) {

    if (!seconds) return '0s'

    const minutes = Math.floor(seconds / 60) % 60
    const hours = Math.floor(seconds / 3600)
    seconds = seconds % 60

    const timeInHours = hours > 0
    const timeInMinutes = minutes > 0

    let output = []

    if (timeInHours) {
        output.push(`${hours}h`)
    }

    // Append minutes to result if time is in minutes or in hours (e.g. "15m 40s" or "1h 00m 59s")
    if (timeInMinutes || timeInHours) {
        // If time is in hours, make sure minutes are 0 prefixed if necessary (e.g. "1h 07m")
        output.push(`${timeInHours ? minutes.toString().padStart(2, '0') : minutes}m`)
    }

    // If time is in hours or minutes, make sure seconds are 0 prefixed if necessary (e.g. "1h 00m 00s" or "5m 07s")
    output.push(`${(timeInHours || timeInMinutes) ? seconds.toString().padStart(2, '0') : seconds}s`)

    return output.join(' ')
}

/**
 * Convert string representation (HH:mm) or (HH:mm:ss) into number of seconds (in that day at that time)
 * @param time {string}
 * @returns {number} Seconds since midnight
 */
export function timeColonDelimitedToSeconds(time) {
    if (!time) return 0;

    const parts = time.split(':');
    if (!parts.length) return 0;

    let seconds = 0;

    seconds += +parts[0] * 3600;
    seconds += +parts[1] * 60;

    if (parts.length > 2) {
        seconds += +parts[2];
    }
    return seconds;
}

/**
 * Return 'ampm' or '24hr' depending on passed timepickerMode preference
 * @param timepickerMode {number} - 0 for browser locale, 1 for ampm, 2 for 24hr
 * @returns {'ampm' | '24hr'} 'ampm' or '24hr'
 */
export function getTimeFormat(timepickerMode) {
    switch (timepickerMode) {
        case 0:
            return Intl.DateTimeFormat(navigator.language, { hour: 'numeric' }).resolvedOptions().hour12 ? 'ampm' : '24hr';
        case 1:
            return 'ampm';
        case 2:
        default:
            return '24hr';
    }
}

/**
 * Format given time components into string with given format e.g. '07:25 pm' or '23:59'
 * @param time {Object} - Object with time components to format
 * @param time.h {number} - Hours
 * @param time.m {number} - Minutes
 * @param time.s {number=} - Seconds
 * @param format {'ampm' | '24hr'} - 'ampm' or '24hr'
 * @returns {string} Formatted time string e.g. '07:25 pm' or '23:59' or empty string if invalid time given.
 */
export function formatTime(time = {}, format = '24hr') {
    switch (true) {
        case time.h == null:
        case time.m == null:
        case isNaN(time.h):
        case isNaN(time.m):
            return '';
    }

    if(format != '24hr' && format != 'ampm') {
        format = '24hr';
    }

    let ampm = '';
    if (format === 'ampm') {
        if (time.h === 0) {
            ampm = ' am';
            time.h = 12;
        } else if (time.h < 12) {
            ampm = ' am';
        } else {
            if (time.h > 12) {
                time.h -= 12;
            }
            ampm = ' pm';
        }
    }

    const timeSegments = [
        time.h.toString().padStart(2, "0"),
        time.m.toString().padStart(2, "0")
    ];

    if(time.s != null && !isNaN(time.s)) {
        timeSegments.push(time.s.toString().padStart(2, "0"));
    }

    return timeSegments.join(':') + ampm;
}
